import { DestroyRef, inject, Injectable, NgZone } from '@angular/core';

import { EcmDispatcherModule } from '@ecm/dispatcher';

import { Link } from '../business/link/link';
import { LinkService } from './link.service';

const BOTMAN_LISTENER_KEY = '__ECM_BOTMAN_LISTENER__';
const BOTMAN_MESSAGE_NAVIGATE_EVENT = 'botman.navigate';

// Allowed origin(s) – tighten as needed
const ALLOWED_ORIGINS = new Set<string>(['https://front.bot.orange.fr']);
const CLASS_NAME = 'ChatbotNavigationService';

/**
 * Shape of incoming botman navigate messages from iframe postMessage.
 */
interface BotmanNavigatePayload {
    action: typeof BOTMAN_MESSAGE_NAVIGATE_EVENT;
    link: Link;
    [key: string]: unknown;
}
/**
 * We can't initiate this business in the chatbot service from @ecm/consumer, because we need LinkService
 * from @ecm/router, and it would create a circular dependency, so we create this dedicated service in
 * @ecm/router
 */
@Injectable({
    providedIn: 'root',
})
export class ChatbotNavigationService {
    private readonly destroyRef = inject(DestroyRef);
    private readonly linkService = inject(LinkService);

    private readonly zone = inject(NgZone);

    public initBotmanListener(): void {
        // check if listener is already initialized
        // if already initialized, call the abort signal to remove the previous listener
        if (globalThis[BOTMAN_LISTENER_KEY]) {
            globalThis[BOTMAN_LISTENER_KEY].controller?.abort(); // Automatically removes listener
            globalThis[BOTMAN_LISTENER_KEY] = undefined;
            EcmDispatcherModule.sendConsoleDebug(CLASS_NAME, 'Re-initializing botman listener, previous one removed');
        }

        const controller = new AbortController();

        // Define handler outside Angular zone for perf; re-enter only on relevant messages
        const handler = (event: MessageEvent) => {
            // Filter by origin
            if (!ALLOWED_ORIGINS.has(event.origin)) return;

            // Validate structure
            if (!event.data || typeof event.data !== 'object') return;

            // Narrow message to our expected payload
            const payload = event.data;
            if (payload.action !== BOTMAN_MESSAGE_NAVIGATE_EVENT) return;
            EcmDispatcherModule.sendConsoleDebug(
                CLASS_NAME + ' --> handler ~ event is botman.navigate ==> ',
                JSON.stringify(event.data),
            );

            // Re-enter zone for UI state updates / navigation
            this.zone.run(() => {
                this.handleBotmanNavigateEvent(payload);
            });
        };

        // add the event listener with its handler handleBotmanNavigateEvent
        window.addEventListener('message', handler, { signal: controller.signal });

        // once event listener is added, mark it as initialized
        // Store marker
        globalThis[BOTMAN_LISTENER_KEY] = {
            controller,
            handler,
            type: 'message',
        };
        // Cleanup on service destroy (abort removes listener)
        this.destroyRef.onDestroy(() => {
            // if an instance exists and matches current controller, abort to remove listener
            if (globalThis[BOTMAN_LISTENER_KEY]?.controller === controller) {
                controller.abort();
                globalThis[BOTMAN_LISTENER_KEY] = undefined;
            }
        });
    }

    private readonly handleBotmanNavigateEvent = (payload: BotmanNavigatePayload): void => {
        EcmDispatcherModule.sendConsoleDebug(CLASS_NAME, '🚀 ' + JSON.stringify(payload));
        this.linkService.openLink(payload.link, {});
    };
}
